import * as Sentry from '@sentry/ember';
import {
  hasConsent,
  OneTrustConsent,
  onFirstConsent,
} from '@square/dashboard-utilities/utils/one-trust/one-trust-compliance';
import Service from '@ember/service';
import { assert } from '@ember/debug';
import { CDP } from '@squareup/cdp';
import ENV from '../config/environment';
import envUtil from '@square/environment-utils/environment';
import { getOwner } from '@ember/application';
import { getUrlFlag } from '@square/dashboard-utilities/utils/window';

// Copied from: dashboard/app/services/data-api.js and
// https://github.com/squareup/onboard-frontend/pull/324/files#diff-9764cd8b6365b92786ac2e7eecab5e31a51193664db8931af01778ab9d0fa76c
// Used to send information to Customer Data Platform

export default class DataApiService extends Service {
  cdp = null; // Exposes instance of CDP library for this service's CDP source

  constructor(...args) {
    super(...args);

    onFirstConsent(getOwner(this), OneTrustConsent.TARGETING, () => {
      const apiKey = envUtil().isProduction
        ? ENV.dataApi.production
        : ENV.dataApi.staging;
      const environment = envUtil().isProduction ? 'production' : 'staging';

      this.cdp = new CDP({
        apiKey,
        environment,
        application: { name: 'app-dashboard' }, // Registry app for DS event ownership attribution
        logEventsToConsole: this.#logEventsToConsole(),
        useSameOrigin: envUtil().isProduction || envUtil().isStaging,
      });
    });
  }

  willDestroy() {
    this.cdp?.removeEventListeners();
  }

  #logEventsToConsole() {
    if (getUrlFlag('logcdp', location)) {
      return 'on';
    }
    if (envUtil().isTesting) {
      return 'off';
    }
    return 'default';
  }

  // identify is called in app/routes/application.js once we've fetched tracking data
  // it allows us to identify the user prior to calling `track`
  identify({
    merchantToken,
    personToken,
    employmentToken,
    customTraits = {},
    options = {},
  }) {
    onFirstConsent(getOwner(this), OneTrustConsent.TARGETING, () => {
      assert('Merchant token cannot be falsey', merchantToken);
      if (!merchantToken) {
        Sentry.captureException(
          new Error('Tried to call data-api identify without a merchant token')
        );
      }

      const traits = {
        merchantToken,
        employmentToken,
        personToken,
        ...customTraits,
      };

      this.cdp.identify(merchantToken, 'merchant', traits, {
        overrides: options,
      });
    });
  }

  track(eventName, properties = {}, context = {}) {
    if (!hasConsent(OneTrustConsent.TARGETING) || !eventName) {
      return;
    }
    const updatedContext = { ...context };

    this.cdp.track(eventName, properties, {
      overrides: { context: updatedContext },
    });
  }
}
